import { useState, useEffect, useCallback } from 'react';
import { signIn as apiSignIn, signInWithGoogle } from '../api';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

export default function SignIn({ navigate }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const s = {
    wrap: { maxWidth: 380, margin: '80px auto 0' },
    logo: { fontSize: 24, fontWeight: 700, color: 'var(--accent2)', letterSpacing: '-0.02em', marginBottom: 32, display: 'block' },
    logoSpan: { color: 'var(--text)' },
    h1: { fontSize: 22, fontWeight: 600, marginBottom: 8 },
    sub: { color: 'var(--muted)', fontSize: 14, marginBottom: 28, lineHeight: 1.5 },
    label: { display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text)' },
    input: { width: '100%', padding: '12px 14px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 15, outline: 'none', marginBottom: 16, boxSizing: 'border-box' },
    btn: { width: '100%', padding: '12px', borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 600, fontSize: 15, cursor: 'pointer' },
    error: { color: 'var(--red)', fontSize: 13, padding: '10px 14px', background: 'rgba(239,68,68,0.08)', borderRadius: 'var(--radius-sm)', marginBottom: 12 },
    info: { color: 'var(--green)', fontSize: 13, padding: '10px 14px', background: 'rgba(34,197,94,0.08)', borderRadius: 'var(--radius-sm)', marginBottom: 12 },
    hint: { textAlign: 'center', fontSize: 13, color: 'var(--muted)', marginTop: 20 },
    link: { color: 'var(--accent2)', cursor: 'pointer', fontWeight: 500 },
    divider: { display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0', color: 'var(--muted)', fontSize: 13 },
    dividerLine: { flex: 1, height: 1, background: 'var(--border)' },
    googleBtn: {
      width: '100%', padding: '11px 16px', borderRadius: 'var(--radius-sm)',
      border: '1px solid var(--border)', background: 'var(--bg2)',
      color: 'var(--text)', fontWeight: 500, fontSize: 14, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
    },
  };

  const handleGoogleResponse = useCallback(async (response) => {
    if (!response.credential) return;
    setLoading(true); setError('');
    try {
      const result = await signInWithGoogle(response.credential);
      if (result.token) {
        navigate('consumer');
      } else {
        setError(result.error || 'Google sign-in failed');
      }
    } catch {
      setError('Could not connect. Try again.');
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
        });
        // Render button into container
        const container = document.getElementById('google-signin-btn');
        if (container) {
          window.google.accounts.id.renderButton(container, {
            theme: 'filled_black', size: 'large', width: '380',
            text: 'continue_with', shape: 'rectangular',
          });
        }
      }
    };
    document.body.appendChild(script);
    return () => { try { document.body.removeChild(script); } catch {} };
  }, [handleGoogleResponse]);

  async function handleSignIn() {
    if (!email.trim()) { setError('Please enter your email'); return; }
    if (!password) { setError('Please enter your password'); return; }
    setLoading(true); setError(''); setInfo('');
    try {
      const result = await apiSignIn(email.trim(), password);
      if (result.token) {
        if (result.passwordSet) {
          setInfo('Password set successfully!');
        }
        navigate('consumer');
      } else {
        setError(result.error || 'Sign in failed. Check your credentials.');
      }
    } catch {
      setError('Could not connect. Try again.');
    }
    setLoading(false);
  }

  return (
    <div style={s.wrap}>
      <span style={s.logo}>invoke<span style={s.logoSpan}>API</span></span>
      <h1 style={s.h1}>Sign in</h1>
      <p style={s.sub}>Enter your credentials to access your API keys and credits.</p>

      {/* Google Sign-In */}
      {GOOGLE_CLIENT_ID && (
        <>
          <div id="google-signin-btn" style={{ marginBottom: 4 }} />
          <div style={s.divider}>
            <div style={s.dividerLine} />
            <span>or</span>
            <div style={s.dividerLine} />
          </div>
        </>
      )}

      <label style={s.label}>Email</label>
      <input style={s.input} type="email" placeholder="you@example.com"
             value={email} onChange={e => setEmail(e.target.value)} autoFocus={!GOOGLE_CLIENT_ID} />

      <label style={s.label}>Password</label>
      <input style={s.input} type="password" placeholder="Your password"
             value={password} onChange={e => setPassword(e.target.value)}
             onKeyDown={e => e.key === 'Enter' && handleSignIn()} />

      {error && <div style={s.error}>{error}</div>}
      {info && <div style={s.info}>{info}</div>}

      <button style={s.btn} onClick={handleSignIn} disabled={loading}>
        {loading ? 'Signing in...' : 'Sign in'}
      </button>

      <p style={s.hint}>
        No account yet?{' '}
        <span style={s.link} onClick={() => navigate('getkey')}>Create account</span>
      </p>
    </div>
  );
}
